import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { withTranslation } from '../../../ChildrenContext.js';
import PropTypes from 'prop-types';
import moment from 'moment';
import StatisticsByCreativeFilter from '../StatisticsByCreativeFilter';
import StatisticsByCreativeTableComponent from '../StatisticsByCreativeTable/StatisticsByCreativeTable.container';
import StatisticsByCreativeMetricsComponent from '../StatisticsByCreativeMetrics/StatisticsByCreativeMetrics.container';
import StatisticsByCreativeSegmentsComponent from '../StatisticsByCreativeSegments/StatisticsByCreativeSegments.container';
import Pagination from '../../../Pagination';
import StatisticsByCreativeCSV from '../StatisticsByCreativeCSV/StatisticsByCreativeCSV.container';
import Loading from '../../../Loading';
import config from '../../../../config';
import objectHasChanged from '../../../../helpers/objectHasChanged';
import query from '../../../../helpers/query';
import { sanitizeSegments, areSegmentsEqual } from '../../../../helpers/segmentUtils';
import { isSameFilterValues } from '../../../../helpers/reportFilterReset';
import ReportByCreativeComponent from '../../../../utils/ReportByCreativeComponent/ReportByCreativeComponent';

const defDataSort = {
    sort: 'GroupBy',
    order: 'desc'
};

export const StatisticsByCreativeContent = ({
    location,
    metrics: propMetrics,
    segments: propSegments,
    page: propPage,
    totalPages: propTotalPages,
    loadingData: propLoadingData,
    search,
    t,
    serverStorageReady
}) => {
    const reportUtils = ReportByCreativeComponent.utils;
    const parsedQuery = useMemo(() => query.parse(location && location.query), [location]);
    const hasQueryMetrics = Boolean(parsedQuery.metrics);
    const defaultMetricsRef = useRef(reportUtils.getStrMetric());

    const getDateFromString = useCallback((date) => {
        if (date) {
            date = new Date(date);
            return moment().set({'year': date.getFullYear(), 'month': date.getMonth(), 'date': date.getDate()});
        }
        return false;
    }, []);

    const [startDate, setStartDate] = useState(() => {
        let date = getDateFromString(parsedQuery.startDate);
        if (date) {
            return date.startOf('day');
        } else {
            return moment().subtract(6, 'days').startOf('day');
        }
    });
    const [endDate, setEndDate] = useState(() => {
        let date = getDateFromString(parsedQuery.endDate);
        if (date) {
            return date.endOf('day');
        } else {
            return moment().endOf('day');
        }
    });
    const [bindActions, setBindActions] = useState(parsedQuery.bindActions || config.defaultBindActions);
    const [groupBy, setGroupBy] = useState(parsedQuery.groupBy || config.defaultGroupBy);
    const [count, setCount] = useState(parsedQuery.count || config.defaultCount);
    const [metrics, setMetrics] = useState(parsedQuery.metrics || propMetrics || '');
    const [sort, setSort] = useState(parsedQuery.sort || defDataSort.sort);
    const [order, setOrder] = useState(parsedQuery.order || defDataSort.order);
    const sanitizedInitialSegments = useMemo(
        () => sanitizeSegments(parsedQuery.segments || propSegments),
        [parsedQuery.segments, propSegments]
    );
    const [segments, setSegments] = useState(sanitizedInitialSegments);
    const [page, setPage] = useState(parsedQuery.page || +propPage);
    const [totalPages, setTotalPages] = useState(propTotalPages);
    const [loadingData, setLoadingData] = useState(propLoadingData);

    const searchState = useMemo(() => ({
        startDate, endDate, bindActions, groupBy, count, metrics, sort, order, segments, page, totalPages, loadingData
    }), [startDate, endDate, bindActions, groupBy, count, metrics, sort, order, segments, page, totalPages, loadingData]);

    const stateRef = useRef(searchState);
    const searchRef = useRef(search);
    const callSearch = useCallback((params) => {
        if (!params.metrics) {
            return;
        }
        searchRef.current && searchRef.current(params);
    }, []);
    useLayoutEffect(() => {
        stateRef.current = searchState;
        searchRef.current = search;
    }, [searchState, search]);

    const isStateSearchChanged = React.useMemo(() => objectHasChanged(['metrics', 'page', 'totalPages', 'loadingData'], true, searchState), [searchState]);

    const pushQuery = useCallback(() => {
        const current = stateRef.current;
        query.replace({
            startDate: current.startDate.startOf('day').format(config.reportHourFormat),
            endDate: current.endDate.endOf('day').format(config.reportHourFormat),
            groupBy: current.groupBy || null,
            count: current.count || null,
            bindActions: current.bindActions,
            sort: current.sort,
            order: current.order,
            metrics: current.metrics || null,
            page: current.page,
            segments: current.segments && Object.keys(current.segments || {}).length ? JSON.stringify(current.segments) : null
        });
    }, []);

    useEffect(() => {
        if (hasQueryMetrics) {
            return;
        }

        if (typeof propMetrics === 'string' && propMetrics.length) {
            setMetrics((prev) => (prev === propMetrics ? prev : propMetrics));
            return;
        }

        if (serverStorageReady && !metrics) {
            setMetrics((prev) => prev || defaultMetricsRef.current);
        }
    }, [propMetrics, serverStorageReady, hasQueryMetrics, metrics]);

    useEffect(() => {
        setSegments(prevSegments =>
            areSegmentsEqual(prevSegments, sanitizedInitialSegments) ? prevSegments : sanitizedInitialSegments
        );
    }, [sanitizedInitialSegments]);

    useEffect(() => {
        setTotalPages(propTotalPages);
        setLoadingData(propLoadingData);
        
        const current = stateRef.current;
        if (current.totalPages !== propTotalPages || current.loadingData !== propLoadingData) {
            stateRef.current = {
                ...current,
                totalPages: propTotalPages,
                loadingData: propLoadingData
            };
        }
    }, [propTotalPages, propLoadingData]);

    const onUpdateDate = useCallback((index) => {
        if (loadingData) {
            return;
        }

        const current = stateRef.current;
        const isStateChanged = objectHasChanged(['metrics', 'page', 'totalPages', 'loadingData'], true, current);

        const nextPage = isStateChanged ? config.initialPage : current.page;
        const nextSort = defDataSort.sort;
        const nextOrder = defDataSort.order;

        setPage(nextPage);
        setSort(nextSort);
        setOrder(nextOrder);

        const nextState = {
            ...current,
            sort: nextSort,
            order: nextOrder,
            page: nextPage
        };
        stateRef.current = nextState;
        setTimeout(() => {
            const updated = stateRef.current;
            if (searchRef.current) {
                searchRef.current(updated);
            }
            pushQuery();
        }, 0);
    }, [loadingData, pushQuery]);

    // skips the echo of a reset from the top filter (see helpers/reportFilterReset)
    const resetEchoRef = useRef(null);

    const onActionFilter = useCallback((filterState) => {
        const resetValues = resetEchoRef.current;
        resetEchoRef.current = null;
        if (resetValues && isSameFilterValues(resetValues, filterState)) {
            return;
        }

        const current = stateRef.current;
        const isStateChanged = objectHasChanged(['metrics', 'page', 'totalPages', 'loadingData'], true, current);

        setPage(isStateChanged ? config.initialPage : current.page);
        setStartDate(filterState.startDate);
        setEndDate(filterState.endDate);
        setBindActions(filterState.bindActions);
        setGroupBy(filterState.groupBy);
        setCount(filterState.count);
        
        const nextState = {
            ...current,
            ...filterState,
            page: isStateChanged ? config.initialPage : current.page
        };
        stateRef.current = nextState;
        setTimeout(() => {
            const updated = stateRef.current;
            callSearch(updated);
            pushQuery();
        }, 0);
    }, [pushQuery, callSearch]);

    // Reset all: top filters and filter rows to defaults, one request
    const onResetAll = useCallback(() => {
        const defaults = {
            startDate: moment().subtract(6, 'days').startOf('day'),
            endDate: moment().endOf('day'),
            bindActions: config.defaultBindActions,
            groupBy: config.defaultGroupBy,
            count: config.defaultCount
        };

        setSegments({});
        setSort(defDataSort.sort);
        setOrder(defDataSort.order);
        setPage(config.initialPage);
        stateRef.current = { ...stateRef.current, segments: {}, ...defDataSort, page: config.initialPage };

        onActionFilter(defaults);
        resetEchoRef.current = defaults;
    }, [onActionFilter]);

    const onActionMetrics = useCallback((newMetrics) => {
        setMetrics(newMetrics);
        const current = stateRef.current;
        const nextState = {
            ...current,
            metrics: newMetrics
        };
        stateRef.current = nextState;
        setTimeout(() => {
            const updated = stateRef.current;
            callSearch(updated);
            pushQuery();
        }, 0);
    }, [pushQuery, callSearch]);

    const onActionTable = useCallback((dataSort) => {
        setSort(dataSort.sort);
        setOrder(dataSort.order);
        
        const current = stateRef.current;
        const nextState = {
            ...current,
            ...dataSort
        };
        stateRef.current = nextState;
        setTimeout(() => {
            const updated = stateRef.current;
            callSearch(updated);
            pushQuery();
        }, 0);
    }, [pushQuery, callSearch]);

    const onPageChange = useCallback((newPage) => {
        setPage(newPage);
        
        const current = stateRef.current;
        const nextState = {
            ...current,
            page: newPage
        };
        stateRef.current = nextState;
        setTimeout(() => {
            const updated = stateRef.current;
            callSearch(updated);
            pushQuery();
        }, 0);
    }, [pushQuery, callSearch]);

    const onActionSegments = useCallback((incomingSegments) => {
        setSegments(prevSegments => {
            const sanitized = sanitizeSegments(incomingSegments);
            return areSegmentsEqual(prevSegments, sanitized) ? prevSegments : sanitized;
        });
    }, []);

    if (!metrics) {
        return (
            <section className="wrapper-content statistics">
                <div className="ibox text-center">
                    <Loading center />
                </div>
            </section>
        );
    }

    return (
        <section className="wrapper-content statistics">
            <div className="ibox">
                <StatisticsByCreativeFilter
                    startDate={startDate}
                    endDate={endDate}
                    groupBy={groupBy}
                    count={count}
                    bindActions={bindActions}
                    onAction={onActionFilter}
                />

                <StatisticsByCreativeSegmentsComponent
                    segments={segments}
                    onAction={onActionSegments}
                    onUpdateDate={onUpdateDate}
                    loading={loadingData}
                    onReset={onResetAll}
                    commonControls={true}
                />
            </div>

            <div className="ibox-title bold" id="statistics-table-header">
                <StatisticsByCreativeMetricsComponent onAction={onActionMetrics} metrics={metrics}/>
                <StatisticsByCreativeCSV/>
            </div>

            <div className="ibox">
                <div className="ibox-content no-padding">
                    <StatisticsByCreativeTableComponent
                        sort={sort}
                        order={order}
                        metrics={metrics}
                        onAction={onActionTable}
                    />
                </div>
                <div className="ibox-footer pl0 pr0 pb0">
                    <Pagination
                        onPageChange={onPageChange}
                        initialPage={page}
                        totalPages={totalPages}
                        loading={loadingData}
                        itemsCountPerPage={count}
                        align={'left'}
                    />
                </div>
            </div>
        </section>
    );
};

StatisticsByCreativeContent.propTypes = {
    location: PropTypes.object,
    metrics: PropTypes.string,
    segments: PropTypes.object,
    page: PropTypes.number,
    totalPages: PropTypes.number,
    loadingData: PropTypes.bool,
    search: PropTypes.func,
    t: PropTypes.object,
    serverStorageReady: PropTypes.bool
};

StatisticsByCreativeContent.defaultProps = {
    metrics: '',
    segments: {},
    page: 1,
    totalPages: 0,
    loadingData: false,
    search: () => {},
    serverStorageReady: false
};

export default withTranslation(StatisticsByCreativeContent);
